/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useMemo } from 'preact/hooks';
import type { BCP47Locale } from '#UNSAFE_IntlDateTime';
import { getAmPmStringsForLocale } from '#utils/UNSAFE_timeUtils';

import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import type { BundleType } from '#resources/nls/bundle';

type Props = {
  locale: BCP47Locale;
};

/**
 * Hook to generate ARIA labels for time input fields based on the provided locale.
 *
 * @param {Props} props - Hook properties.
 * @param {BCP47Locale} props.locale - The locale to use for generating ARIA labels.
 *
 * @returns {Object} An object containing ARIA labels for hour, minute, second, millisecond, and day period.
 */
const useTimeAriaLabels = ({ locale }: Props) => {
  const { am, pm } = useMemo(() => {
    return getAmPmStringsForLocale(locale);
  }, [locale]);

  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const ariaLabels = {
    hour: translations.formControl_hour(),
    minute: translations.formControl_minute(),
    second: translations.formControl_second(),
    millisecond: translations.formControl_millisecond(),
    dayPeriod: `${am}/${pm}`
  };

  return ariaLabels;
};

export { useTimeAriaLabels };
